import { useMemo } from 'react'
import {
  PiHandshake,
  PiChat,
  PiPaperPlaneTilt,
  PiTrendUp,
  PiUsers,
} from 'react-icons/pi'

import { Alert } from 'components/FbUI/Alert'
import { TargetArrowIcon } from 'components/Layout/LeftNav/nav-icons'
import { cn } from 'components/UI/cn'
import type { Campaign } from 'models/campaign'
import { WORKFLOW_STATUS } from 'models/campaign'
import { useGetReplies, type Reply } from 'features/campaigns/replies_api'
import {
  useGetCampaignSequence,
  type Sequence,
} from 'features/campaigns/sequences_api'
import {
  useGetCampaignInsights,
  useGetCampaignLaunchReadiness,
} from 'services/campaigns'
import { formatInteger } from 'utils/formatting'

interface CampaignDashboardOverviewTabProps {
  campaign: Campaign
  campaignId: number
  campaignStatus: string
  onTabChange?: (tab: string) => void
  onViewSequence?: () => void
}

const SENTIMENT_CONFIG = {
  positive: {
    label: 'Interested',
    color: 'bg-green-50 text-green-700',
  },
  negative: {
    label: 'Not Interested',
    color: 'bg-gray-100 text-gray-700',
  },
  neutral: {
    label: 'Neutral',
    color: 'bg-white border border-gray-300 text-gray-500',
  },
} as const

function getSentiment(reply: Reply): keyof typeof SENTIMENT_CONFIG {
  if (reply.is_interested === true) return 'positive'
  if (reply.is_interested === false) return 'negative'
  return 'neutral'
}

import { formatRelativeTime } from 'utils/formatting'

function getReplyPreview(reply: Reply): string {
  if (reply.text_body) {
    return (
      reply.text_body.slice(0, 100) +
      (reply.text_body.length > 100 ? '...' : '')
    )
  }
  if (reply.html_body) {
    const stripped = reply.html_body.replace(/<[^>]*>/g, '')
    return stripped.slice(0, 100) + (stripped.length > 100 ? '...' : '')
  }
  return reply.subject || 'No preview available'
}

function toNumber(value: string | number | null | undefined): number {
  if (typeof value === 'number') return value
  if (typeof value === 'string') {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : 0
  }
  return 0
}

export function CampaignDashboardOverviewTab({
  campaign,
  campaignId,
  campaignStatus,
  onTabChange,
  onViewSequence,
}: CampaignDashboardOverviewTabProps) {
  const isPaused =
    campaignStatus === 'paused' ||
    campaignStatus === 'stopped' ||
    campaign.workflow_status === WORKFLOW_STATUS.PAUSED
  const isComplete =
    campaignStatus === 'completed' ||
    campaignStatus === 'archived' ||
    campaignStatus === 'deleted' ||
    campaign.workflow_status === WORKFLOW_STATUS.COMPLETE

  const { data: sequence } = useGetCampaignSequence(campaignId, !!campaign)
  const { data: insights } = useGetCampaignInsights(campaignId, !!campaign)
  const { data: readiness } = useGetCampaignLaunchReadiness(
    campaignId,
    !!campaign
  )
  const bisonCampaignId = sequence?.bison_campaign_id ?? 0
  const { data: repliesData } = useGetReplies({ campaign_id: bisonCampaignId })
  const recentReplies = useMemo(
    () => repliesData?.results.slice(0, 3) ?? [],
    [repliesData]
  )

  const stats = useMemo(
    () => ({
      targets: campaign.contact_company_count ?? 0,
      contacts: campaign.contact_count ?? 0,
      contactsInSequence: readiness?.leads.count,
      deals: campaign.deal_count ?? 0,
      emailsSent: toNumber(insights?.summary.emails_sent),
      replyRate: toNumber(
        insights?.summary.unique_replies_per_contact_percentage
      ),
      interestedRate: toNumber(insights?.summary.interested_percentage),
    }),
    [campaign, insights, readiness]
  )

  const hasInSequenceData =
    stats.contacts > 0 && stats.contactsInSequence !== undefined
  const contactsValue = hasInSequenceData
    ? `${formatInteger(stats.contactsInSequence)} of ${formatInteger(stats.contacts)}`
    : formatInteger(stats.contacts) || '—'
  const contactsSublabel = hasInSequenceData ? 'in sequence' : undefined

  const statCards: Array<{
    label: string
    value: string
    sublabel?: string
    icon: React.ComponentType<{ size?: number; className?: string }>
  }> = [
    {
      label: 'Targets',
      value: formatInteger(stats.targets) || '—',
      icon: TargetArrowIcon,
    },
    {
      label: 'Contacts',
      value: contactsValue,
      sublabel: contactsSublabel,
      icon: PiUsers,
    },
    {
      label: 'Deals',
      value: formatInteger(stats.deals) || '—',
      icon: PiHandshake,
    },
    {
      label: 'Emails Sent',
      value: stats.emailsSent > 0 ? formatInteger(stats.emailsSent) : '—',
      icon: PiPaperPlaneTilt,
    },
    {
      label: 'Reply Rate',
      value: stats.replyRate > 0 ? `${stats.replyRate.toFixed(1)}%` : '—',
      icon: PiChat,
    },
    {
      label: 'Interested Rate',
      value:
        stats.interestedRate > 0 ? `${stats.interestedRate.toFixed(1)}%` : '—',
      icon: PiTrendUp,
    },
  ]

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {statCards.map((stat) => (
          <div
            key={stat.label}
            className="flex items-center gap-3 bg-surface rounded-lg border border-line p-3.5"
          >
            <div className="size-9 rounded-lg bg-brand-active/10 flex items-center justify-center shrink-0 text-brand-active">
              <stat.icon size={16} />
            </div>
            <div className="min-w-0">
              <p className="text-sm text-fg-secondary truncate">{stat.label}</p>
              <p
                className={cn(
                  'text-lg font-bold leading-tight',
                  stat.value === '—' ? 'text-fg-muted' : 'text-fg'
                )}
              >
                {stat.value}
              </p>
              {stat.sublabel && (
                <p className="mt-0.5 text-xs text-fg-secondary">
                  {stat.sublabel}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <div className="rounded-lg border border-line bg-surface p-5 shadow-[0px_1px_3px_0px_rgba(16,24,40,0.08),0px_1px_2px_-1px_rgba(16,24,40,0.04)]">
          <div className="mb-5 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h3 className="text-base text-fg">Sequence Progress</h3>
              {!isComplete && (
                <span
                  className={cn(
                    'rounded-full px-2 py-0.5 text-xs font-medium tracking-[-0.2px]',
                    isPaused
                      ? 'bg-warning-bg-subtle text-warning-strong'
                      : 'bg-success-bg-subtle text-success-strong'
                  )}
                >
                  {isPaused ? 'Paused' : 'Sending'}
                </span>
              )}
            </div>
            {onViewSequence && (
              <button
                type="button"
                onClick={onViewSequence}
                className="text-sm font-medium text-brand-bright hover:text-brand-active tracking-[-0.2px] transition-colors"
              >
                View Sequence
              </button>
            )}
          </div>
          <SequenceProgressSection
            sequence={sequence}
            stepStats={insights?.summary.sequence_step_stats}
            totalContacts={stats.contacts}
            isComplete={isComplete}
          />
        </div>

        <div className="rounded-lg border border-line bg-surface p-5 shadow-[0px_1px_3px_0px_rgba(16,24,40,0.08),0px_1px_2px_-1px_rgba(16,24,40,0.04)]">
          <div className="mb-5 flex items-center justify-between">
            <h3 className="text-base text-fg">Recent Replies</h3>
            {onTabChange && (
              <button
                type="button"
                onClick={() => onTabChange('replies')}
                className="text-sm font-medium text-brand-bright hover:text-brand-active tracking-[-0.2px] transition-colors"
              >
                View All Replies
              </button>
            )}
          </div>
          {recentReplies.length > 0 ? (
            <div className="divide-y divide-gray-200 space-y-0">
              {recentReplies.map((reply) => {
                const sentiment = SENTIMENT_CONFIG[getSentiment(reply)]
                return (
                  <div
                    key={reply.id}
                    className="group cursor-pointer py-3 first:pt-0 last:pb-0 hover:bg-gray-50 -mx-1 rounded px-1 transition-colors"
                    onClick={() => onTabChange?.('replies')}
                  >
                    <div className="mb-1 flex items-center justify-between">
                      <span className="text-sm text-gray-900 group-hover:text-black transition-colors">
                        {reply.from_name || reply.from_email_address}
                      </span>
                      <span className="text-xs text-gray-600">
                        {formatRelativeTime(reply.date_received)}
                      </span>
                    </div>
                    <p className="mb-1.5 truncate text-sm text-gray-700 group-hover:text-gray-900 transition-colors">
                      {getReplyPreview(reply)}
                    </p>
                    <span
                      className={cn(
                        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs',
                        sentiment.color
                      )}
                    >
                      {sentiment.label}
                    </span>
                  </div>
                )
              })}
            </div>
          ) : (
            <div className="py-8 text-center">
              <PiChat className="mx-auto mb-2 size-8 text-gray-400" />
              <p className="text-sm text-gray-600">No replies yet</p>
            </div>
          )}
        </div>
      </div>
      {isComplete && (
        <Alert
          color="success"
          title="Campaign Completed"
          description="This campaign finished sending all configured sequence steps."
        />
      )}
    </div>
  )
}

function SequenceProgressSection({
  sequence,
  stepStats,
  totalContacts,
  isComplete,
}: {
  sequence?: Sequence | null
  stepStats?:
    | Array<{
        sequence_step_id: number
        sent: number
        interested: number
        unique_replies: number
      }>
    | undefined
  totalContacts: number
  isComplete: boolean
}) {
  if (!sequence?.steps?.length) {
    return (
      <div className="py-8 text-center">
        <PiPaperPlaneTilt className="mx-auto mb-2 size-8 text-fg-muted" />
        <p className="text-sm text-fg-secondary">No sequence steps yet</p>
      </div>
    )
  }

  return (
    <div className="space-y-5">
      {sequence.steps.map((step, index) => {
        const stat = stepStats?.find(
          (stepStat) => stepStat.sequence_step_id === step.id
        )
        const totalSent = stat?.sent ?? 0
        const interested = stat?.interested ?? 0
        const replied = stat?.unique_replies ?? 0
        const progress =
          isComplete && totalSent > 0
            ? 100
            : totalContacts > 0
              ? Math.min(100, Math.round((totalSent / totalContacts) * 100))
              : 0

        return (
          <div key={step.id}>
            <div className="mb-2 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <span className="w-3 text-sm text-gray-600">{index + 1}</span>
                <span className="max-w-[220px] truncate text-sm text-gray-900">
                  {step.email_subject || `Step ${index + 1}`}
                </span>
              </div>
              <span className="text-sm text-gray-600">{progress}%</span>
            </div>
            <div className="ml-5">
              <div className="h-2.5 w-full overflow-hidden rounded-full bg-gray-200">
                <div
                  className={cn(
                    'h-full rounded-full transition-all',
                    progress > 0 ? 'bg-brand-bright' : 'bg-gray-200'
                  )}
                  style={{ width: `${progress}%` }}
                />
              </div>
              <div className="mt-1.5 flex items-center gap-4">
                <span className="text-xs text-gray-600">{totalSent} sent</span>
                <span className="text-xs text-gray-600">
                  {interested} interested
                </span>
                <span className="text-xs text-gray-600">{replied} replied</span>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default CampaignDashboardOverviewTab
